// Dashboard surface copy — the project landing (header, decisions inbox,
// steering board, roadmap, accordions) plus the per-issue hub (IssuePage)
// and the editor modal. User-facing display text only; control/enum literals,
// chat-seed prompts, and self-labelled send-texts stay frozen elsewhere.

import type { MsgSection } from "./types.js";

export const dashboard: MsgSection = {
  // --- DashHeader ---
  "dashboard.header.decisionsOne": { en: "1 thing needs your call.", de: "1 Sache wartet auf deine Entscheidung." },
  "dashboard.header.decisionsMany": {
    en: "{n} things need your call.",
    de: "{n} Sachen warten auf deine Entscheidung.",
  },
  "dashboard.header.openOne": { en: "1 request in flight.", de: "1 Anfrage in Arbeit." },
  "dashboard.header.openMany": { en: "{n} requests in flight.", de: "{n} Anfragen in Arbeit." },
  "dashboard.header.nothingPending": {
    en: "Nothing pending. Everything is flowing.",
    de: "Nichts offen, alles läuft.",
  },
  "dashboard.header.subDecisions": {
    en: "Everything else is flowing. Here's where things stand — and the levers to steer.",
    de: "Der Rest läuft. Hier siehst du den Stand der Dinge – und die Hebel zum Steuern.",
  },
  "dashboard.header.subDefault": {
    en: "Steer from below — the KPIs read the project's flow, and you can create new requests any time.",
    de: "Steuere von unten – die KPIs zeigen, wie das Projekt läuft, und du kannst jederzeit neue Anfragen aufgeben.",
  },
  "dashboard.header.askLoom": { en: "Ask Loom", de: "Loom fragen" },
  "dashboard.header.newRequest": { en: "New request", de: "Neue Anfrage" },

  // --- TopBar ---
  "dashboard.topbar.health.green": { en: "On track", de: "Auf Kurs" },
  "dashboard.topbar.health.amber": { en: "Watch", de: "Im Auge behalten" },
  "dashboard.topbar.health.red": { en: "At risk", de: "Gefährdet" },
  "dashboard.topbar.checking": { en: "Checking…", de: "Wird geprüft…" },
  "dashboard.topbar.updated": { en: "updated {when}", de: "aktualisiert {when}" },

  // --- IssueSearch ---
  "dashboard.search.placeholder": {
    en: "Search a request by title or number, then open it…",
    de: "Anfrage nach Titel oder Nummer suchen und öffnen…",
  },
  "dashboard.search.clear": { en: "Clear", de: "Leeren" },
  "dashboard.search.clearAria": { en: "Clear search", de: "Suche leeren" },
  "dashboard.search.empty": { en: "No matching requests.", de: "Keine passenden Anfragen." },
  "dashboard.search.open": { en: "Open", de: "Öffnen" },

  // --- DecisionsInbox ---
  "dashboard.inbox.forYou": { en: "For you", de: "Für dich" },
  "dashboard.inbox.title": { en: "What needs your decision", de: "Was deine Entscheidung braucht" },
  "dashboard.inbox.loadingDecisions": { en: "Loading decisions…", de: "Entscheidungen werden geladen…" },
  "dashboard.inbox.countOpen": { en: "{count} open", de: "{count} offen" },
  "dashboard.inbox.empty": {
    en: "Nothing waiting on you right now.",
    de: "Im Moment wartet nichts auf dich.",
  },
  "dashboard.inbox.tag.clarify": { en: "Waiting on you", de: "Wartet auf dich" },
  "dashboard.inbox.tag.proposal": { en: "Ready to review", de: "Bereit zur Prüfung" },
  "dashboard.inbox.tag.blocked": { en: "Stuck on a detail", de: "Hängt an einem Detail fest" },

  // --- SetupBanner ---
  "dashboard.setup.eyebrow": { en: "Project setup", de: "Projekteinrichtung" },
  "dashboard.setup.suggestedMapping": {
    en: "Suggested mapping from {scope}:: labels ({confidence})",
    de: "Vorgeschlagene Zuordnung aus {scope}::-Labels ({confidence})",
  },
  "dashboard.setup.noMatchingLabels": {
    en: "No matching labels yet — defaults will be used",
    de: "Noch keine passenden Labels – es gelten die Standardwerte",
  },
  "dashboard.setup.decisionLabels": { en: "decision labels", de: "Entscheidungs-Labels" },
  "dashboard.setup.concurrentWrite": {
    en: "Project config changed under you — refresh to see the latest.",
    de: "Die Projektkonfiguration wurde geändert – aktualisiere für den neuesten Stand.",
  },
  "dashboard.setup.refresh": { en: "Refresh", de: "Aktualisieren" },
  "dashboard.setup.hide": { en: "Hide", de: "Ausblenden" },
  "dashboard.setup.customise": { en: "Customise", de: "Anpassen" },
  "dashboard.setup.applying": { en: "Applying…", de: "Wird angewendet…" },
  "dashboard.setup.apply": { en: "Apply", de: "Anwenden" },
  "dashboard.setup.searchLabels": { en: "type to search labels…", de: "tippen, um Labels zu finden…" },
  "dashboard.setup.addLabel": { en: "add label…", de: "Label hinzufügen…" },
  "dashboard.setup.removeLabel": { en: "Remove {label}", de: "{label} entfernen" },
  // stage labels (lowercase, as the banner list renders them)
  "dashboard.setup.stage.needs_input": { en: "needs input", de: "Eingabe nötig" },
  "dashboard.setup.stage.planned": { en: "planned", de: "geplant" },
  "dashboard.setup.stage.in_progress": { en: "in progress", de: "in Arbeit" },
  "dashboard.setup.stage.review": { en: "review", de: "Review" },
  "dashboard.setup.stage.shipped": { en: "shipped", de: "ausgeliefert" },

  // --- SteerBoard ---
  "dashboard.steer.eyebrow": { en: "Steer", de: "Steuern" },
  "dashboard.steer.title": { en: "Your requests", de: "Deine Anfragen" },
  "dashboard.steer.sub": {
    en: "Reprioritise, clarify, or add to the work. Changes flow straight to the triad.",
    de: "Neu priorisieren, klären oder ergänzen. Änderungen gehen direkt an die Triade.",
  },
  "dashboard.steer.filterRequests": { en: "Filter requests", de: "Anfragen filtern" },
  "dashboard.steer.filterTitle": { en: "Active hides shipped work", de: "«Aktiv» blendet Ausgeliefertes aus" },
  "dashboard.steer.active": { en: "Active", de: "Aktiv" },
  "dashboard.steer.all": { en: "All", de: "Alle" },
  "dashboard.steer.newRequest": { en: "New request", de: "Neue Anfrage" },
  "dashboard.steer.loadingRequests": { en: "Loading requests…", de: "Anfragen werden geladen…" },

  // --- SteerGroup (stage meta) ---
  "dashboard.stage.needs_input.label": { en: "Needs your input", de: "Braucht deine Eingabe" },
  "dashboard.stage.needs_input.note": { en: "not ready to build", de: "noch nicht baubereit" },
  "dashboard.stage.planned.label": { en: "Planned", de: "Geplant" },
  "dashboard.stage.planned.note": { en: "ready to pick up", de: "kann übernommen werden" },
  "dashboard.stage.in_progress.label": { en: "In progress", de: "In Arbeit" },
  "dashboard.stage.in_progress.note": { en: "team is building", de: "Team baut gerade" },
  "dashboard.stage.review.label": { en: "For your review", de: "Für deine Prüfung" },
  "dashboard.stage.review.note": { en: "awaiting sign-off", de: "wartet auf Freigabe" },
  "dashboard.stage.shipped.label": { en: "Shipped", de: "Ausgeliefert" },
  "dashboard.stage.shipped.note": { en: "delivered", de: "geliefert" },

  // --- SteerRow ---
  "dashboard.steerRow.priorityTitle": { en: "{priority} priority", de: "Priorität {priority}" },
  "dashboard.steerRow.needsDetail": { en: "needs detail", de: "braucht mehr Details" },
  "dashboard.steerRow.verified": { en: "verified", de: "verifiziert" },
  "dashboard.steerRow.milestoneTitle": { en: "Milestone: {title}", de: "Meilenstein: {title}" },
  "dashboard.steerRow.chatTitle": { en: "Chat with Loom about this", de: "Mit Loom darüber chatten" },
  "dashboard.steerRow.chatAria": { en: "Chat with Loom about {key}", de: "Mit Loom über {key} chatten" },
  "dashboard.steerRow.editTitle": { en: "Edit request", de: "Anfrage bearbeiten" },
  "dashboard.steerRow.pt": { en: "pt", de: "Pkt" },

  // --- Roadmap ---
  "dashboard.roadmap.eyebrow": { en: "Roadmap", de: "Roadmap" },
  "dashboard.roadmap.title": { en: "Milestones", de: "Meilensteine" },
  "dashboard.roadmap.sub": {
    en: "Time-boxed containers for related stories. Use them for releases or for tracking the decomposition of a big request.",
    de: "Zeitlich begrenzte Container für zusammengehörige Stories. Gut für Releases oder um zu verfolgen, wie eine grosse Anfrage aufgeteilt wird.",
  },
  "dashboard.roadmap.cancel": { en: "Cancel", de: "Abbrechen" },
  "dashboard.roadmap.newMilestone": { en: "New milestone", de: "Neuer Meilenstein" },
  "dashboard.roadmap.fieldTitle": { en: "Title", de: "Titel" },
  "dashboard.roadmap.titlePlaceholder": { en: "e.g. v1.4 — invoicing", de: "z. B. v1.4 — Rechnungsstellung" },
  "dashboard.roadmap.fieldDescription": { en: "Description", de: "Beschreibung" },
  "dashboard.roadmap.descriptionPlaceholder": {
    en: "What does this milestone deliver?",
    de: "Was bringt dieser Meilenstein?",
  },
  "dashboard.roadmap.fieldStart": { en: "Start date", de: "Startdatum" },
  "dashboard.roadmap.fieldDue": { en: "Due date", de: "Fälligkeitsdatum" },
  "dashboard.roadmap.titleRequired": { en: "Title is required.", de: "Titel ist erforderlich." },
  "dashboard.roadmap.creating": { en: "Creating…", de: "Wird erstellt…" },
  "dashboard.roadmap.createMilestone": { en: "Create milestone", de: "Meilenstein erstellen" },
  "dashboard.roadmap.loadingMilestones": { en: "Loading milestones…", de: "Meilensteine werden geladen…" },
  "dashboard.roadmap.empty": {
    en: "No milestones yet. Create one to start grouping issues into time-boxed deliverables.",
    de: "Noch keine Meilensteine. Erstelle einen und bündle Issues zu zeitlich begrenzten Lieferpaketen.",
  },
  "dashboard.roadmap.noWindow": { en: "No window set", de: "Kein Zeitfenster gesetzt" },
  "dashboard.roadmap.issuesCount": { en: "{closed} / {total} issues", de: "{closed} / {total} Issues" },
  "dashboard.roadmap.points": { en: "{weight} pts", de: "{weight} Pkt" },

  // --- Accordions (titles passed to Accordion) ---
  "dashboard.accordion.projectActivity": { en: "Project activity", de: "Projektaktivität" },
  "dashboard.accordion.recent": { en: "recent", de: "kürzlich" },
  "dashboard.accordion.conversations": { en: "Conversations", de: "Gespräche" },

  // --- ActivityBody ---
  "dashboard.activity.loading": { en: "Loading…", de: "Wird geladen…" },
  "dashboard.activity.empty": {
    en: "No recent activity in the last 7 days.",
    de: "Keine Aktivität in den letzten 7 Tagen.",
  },

  // --- ThreadsBody ---
  "dashboard.threads.loading": { en: "Loading…", de: "Wird geladen…" },
  "dashboard.threads.empty": {
    en: "No conversations yet. Open the drawer and ask Loom anything.",
    de: "Noch keine Gespräche. Öffne die Leiste und frag Loom, was du willst.",
  },
  "dashboard.threads.live": { en: "live", de: "live" },
  "dashboard.threads.session": { en: "session", de: "Sitzung" },

  // --- ProposalsPanel ---
  "dashboard.proposals.relatedWork": { en: "Related work", de: "Verwandte Arbeit" },
  "dashboard.proposals.looking": { en: "Looking…", de: "Wird gesucht…" },
  "dashboard.proposals.candidatesOne": { en: "{count} candidate", de: "{count} Kandidat" },
  "dashboard.proposals.candidatesMany": { en: "{count} candidates", de: "{count} Kandidaten" },
  "dashboard.proposals.andMore": { en: "and {count} more…", de: "und {count} weitere…" },
  "dashboard.proposals.skipLink": { en: "Skip this link", de: "Diesen Link überspringen" },
  "dashboard.proposals.acceptLink": { en: "Accept this link", de: "Diesen Link übernehmen" },
  "dashboard.proposals.linked": { en: "Linked", de: "Verknüpft" },
  "dashboard.proposals.link": { en: "Link", de: "Verknüpfen" },
  "dashboard.proposals.subIssues": { en: "Proposed sub-issues", de: "Vorgeschlagene Sub-Issues" },
  "dashboard.proposals.splitting": { en: "Splitting…", de: "Wird aufgeteilt…" },
  "dashboard.proposals.invest.small": { en: "Small", de: "Klein" },
  "dashboard.proposals.invest.independent": { en: "Independent", de: "Unabhängig" },
  "dashboard.proposals.invest.testable": { en: "Testable", de: "Testbar" },
  "dashboard.proposals.skipChild": { en: "Skip this child", de: "Dieses Sub-Issue überspringen" },
  "dashboard.proposals.createChild": { en: "Create this child", de: "Dieses Sub-Issue erstellen" },
  "dashboard.proposals.selected": { en: "Selected", de: "Ausgewählt" },
  "dashboard.proposals.select": { en: "Select", de: "Auswählen" },

  // --- IssueLinksPanel ---
  "dashboard.links.title": { en: "Linked work", de: "Verknüpfte Arbeit" },
  "dashboard.links.empty": {
    en: "No linked issues or body references on this issue yet.",
    de: "Noch keine verknüpften Issues und keine Verweise im Text dieses Issues.",
  },
  "dashboard.links.gitlabLinks": { en: "Linked issues", de: "Verknüpfte Issues" },
  "dashboard.links.referencedInBody": { en: "Referenced in body", de: "Im Text erwähnt" },
  "dashboard.links.referencedTitle": {
    en: "Issues mentioned in the body via #NNN references but not linked.",
    de: "Issues, die im Text per #NNN erwähnt, aber nicht verknüpft sind.",
  },
  "dashboard.links.edge.relates_to": { en: "relates to", de: "bezieht sich auf" },
  "dashboard.links.edge.blocks": { en: "blocks", de: "blockiert" },
  "dashboard.links.edge.is_blocked_by": { en: "blocked by", de: "blockiert durch" },
  // issue stage labels (capitalised, shared by IssueLinksPanel + IssuePage)
  "dashboard.issueStage.needs_input": { en: "Needs input", de: "Eingabe nötig" },
  "dashboard.issueStage.planned": { en: "Planned", de: "Geplant" },
  "dashboard.issueStage.in_progress": { en: "In progress", de: "In Arbeit" },
  "dashboard.issueStage.review": { en: "For review", de: "Zur Prüfung" },
  "dashboard.issueStage.shipped": { en: "Shipped", de: "Ausgeliefert" },

  // --- LinkedMrsPanel ---
  "dashboard.mrs.title": { en: "Linked code", de: "Verknüpfter Code" },
  "dashboard.mrs.empty": {
    en: "No merge requests reference this issue.",
    de: "Noch kein Merge Request verweist auf dieses Issue.",
  },
  "dashboard.mrs.state.opened": { en: "Open", de: "Offen" },
  "dashboard.mrs.state.merged": { en: "Merged", de: "Gemergt" },
  "dashboard.mrs.state.closed": { en: "Closed", de: "Geschlossen" },
  "dashboard.mrs.state.locked": { en: "Locked", de: "Gesperrt" },
  "dashboard.mrs.mergedToday": { en: "merged today", de: "heute gemergt" },
  "dashboard.mrs.mergedAgo": { en: "merged {ago}", de: "gemergt {ago}" },

  // --- IssueActivityTimeline ---
  "dashboard.issueActivity.loading": { en: "Loading…", de: "Wird geladen…" },
  "dashboard.issueActivity.empty": { en: "No activity yet.", de: "Noch keine Aktivität." },

  // --- IssueTimeline ---
  "dashboard.issueTimeline.loading": { en: "Loading…", de: "Wird geladen…" },
  "dashboard.issueTimeline.empty": { en: "No history yet.", de: "Noch keine Historie." },
  "dashboard.issueTimeline.entered": { en: "Entered {stage}", de: "{stage} betreten" },
  "dashboard.issueTimeline.left": { en: "Left {stage}", de: "{stage} verlassen" },
  "dashboard.issueTimeline.added": { en: "Added {label}", de: "{label} hinzugefügt" },
  "dashboard.issueTimeline.removed": { en: "Removed {label}", de: "{label} entfernt" },

  // --- ComplexitySuggestion ---
  "dashboard.complexity.title": {
    en: "{key} looks complex — {score}/100",
    de: "{key} wirkt komplex – {score}/100",
  },
  "dashboard.complexity.creating": { en: "Creating…", de: "Wird erstellt…" },
  "dashboard.complexity.makeMilestone": { en: "Make milestone", de: "Zum Meilenstein machen" },
  "dashboard.complexity.dismiss": { en: "Dismiss", de: "Verwerfen" },

  // --- ChatDrawer ---
  "dashboard.drawer.askLoom": { en: "Ask Loom", de: "Loom fragen" },
  "dashboard.drawer.closeTitle": { en: "Close (Esc)", de: "Schliessen (Esc)" },
  "dashboard.drawer.closeAria": { en: "Close chat drawer", de: "Chat-Leiste schliessen" },
  "dashboard.drawer.starting": { en: "Starting a new conversation…", de: "Neues Gespräch wird gestartet…" },
  "dashboard.drawer.preparing": { en: "Preparing chat…", de: "Chat wird vorbereitet…" },

  // --- DashboardPage (FAB + toasts) ---
  "dashboard.fab.openChat": { en: "Open chat", de: "Chat öffnen" },
  "dashboard.fab.askLoom": { en: "Ask Loom", de: "Loom fragen" },
  "dashboard.toast.notFoundIid": {
    en: "Couldn't find #{iid} in this project.",
    de: "#{iid} gibt es in diesem Projekt nicht.",
  },
  "dashboard.toast.notFoundRef": {
    en: "Couldn't find {ref} in this project.",
    de: "{ref} gibt es in diesem Projekt nicht.",
  },
  "dashboard.toast.decomposeSkipped": {
    en: "Splitting into sub-issues isn't available right now.",
    de: "Das Aufteilen in Sub-Issues ist momentan nicht verfügbar.",
  },
  "dashboard.toast.decomposeFailed": {
    en: "Splitting into sub-issues failed. Please try again.",
    de: "Das Aufteilen in Sub-Issues ist fehlgeschlagen. Bitte versuch es erneut.",
  },
  "dashboard.toast.cantDecompose": {
    en: "Can't decompose a proposal without an issue id.",
    de: "Ohne Issue-ID lässt sich ein Vorschlag nicht aufteilen.",
  },

  // --- MarkdownEditor (fallbacks) ---
  "dashboard.markdown.placeholder": {
    en: "Describe the request in markdown…",
    de: "Beschreib die Anfrage in Markdown…",
  },
  "dashboard.markdown.ariaLabel": { en: "Markdown editor", de: "Markdown-Editor" },

  // --- Spinner (fallback aria-label) ---
  "dashboard.spinner.loading": { en: "Loading", de: "Wird geladen" },

  // --- IssueEditor ---
  "dashboard.editor.newRequest": { en: "New request", de: "Neue Anfrage" },
  "dashboard.editor.editing": { en: "Editing {key}", de: "{key} bearbeiten" },
  "dashboard.editor.tellLoom": { en: "Tell Loom what you need", de: "Sag Loom, was du brauchst" },
  "dashboard.editor.clarify": { en: "Clarify this request", de: "Diese Anfrage klären" },
  "dashboard.editor.fieldTitle": { en: "Title", de: "Titel" },
  "dashboard.editor.titlePlaceholder": {
    en: "e.g. Show VAT per line item on the customer invoice",
    de: "z. B. MwSt. pro Position auf der Kundenrechnung anzeigen",
  },
  "dashboard.editor.whatYouNeed": { en: "What you need", de: "Was du brauchst" },
  "dashboard.editor.hintOwnWords": { en: "— in your own words", de: "— in deinen eigenen Worten" },
  "dashboard.editor.descriptionPlaceholder": {
    en: "Describe the request the way you'd explain it to a colleague.",
    de: "Beschreib die Anfrage so, wie du sie einer Kollegin erklären würdest.",
  },
  "dashboard.editor.businessOutcome": { en: "Business outcome", de: "Geschäftlicher Nutzen" },
  "dashboard.editor.hintWhyMatters": { en: "— why it matters", de: "— warum es zählt" },
  "dashboard.editor.outcomePlaceholder": {
    en: "e.g. Customers can reconcile VAT without calling support",
    de: "z. B. Kunden können die MwSt. ohne Anruf beim Support abgleichen",
  },
  "dashboard.editor.acceptanceCriteria": { en: "Acceptance criteria", de: "Akzeptanzkriterien" },
  "dashboard.editor.hintHowDone": { en: "— how we'll know it's done", de: "— woran wir sehen, dass es fertig ist" },
  "dashboard.editor.acPlaceholder": { en: "Given… when… then…", de: "Gegeben… wenn… dann…" },
  "dashboard.editor.addCriterion": { en: "Add criterion", de: "Kriterium hinzufügen" },
  "dashboard.editor.history": { en: "History", de: "Historie" },
  "dashboard.editor.priority": { en: "Priority", de: "Priorität" },
  "dashboard.editor.milestone": { en: "Milestone", de: "Meilenstein" },
  "dashboard.editor.hintGroupStories": {
    en: "— group with related stories",
    de: "— mit verwandten Stories bündeln",
  },
  "dashboard.editor.noMilestone": { en: "No milestone", de: "Kein Meilenstein" },
  "dashboard.editor.closedSuffix": { en: " (closed)", de: " (geschlossen)" },
  "dashboard.editor.readyToBuild": { en: "Ready to build", de: "Baubereit" },
  "dashboard.editor.readinessPct": { en: "Readiness {pct}%", de: "Bereitschaft {pct}%" },
  "dashboard.editor.meetsDor": {
    en: "Meets the Definition of Ready",
    de: "Erfüllt die Definition of Ready",
  },
  "dashboard.editor.checksProgress": {
    en: "{passed} of {total} checks",
    de: "{passed} von {total} Prüfungen",
  },
  "dashboard.editor.dorOff": {
    en: "Readiness check unavailable right now",
    de: "Bereitschaftsprüfung momentan nicht verfügbar",
  },
  "dashboard.editor.cancel": { en: "Cancel", de: "Abbrechen" },
  "dashboard.editor.saving": { en: "Saving…", de: "Wird gespeichert…" },
  "dashboard.editor.createRequest": { en: "Create request", de: "Anfrage erstellen" },
  "dashboard.editor.saveChanges": { en: "Save changes", de: "Änderungen speichern" },
  // DoR check labels (shared with IssuePage via buildDorChecks)
  "dashboard.dorCheck.title": { en: "Has a clear, specific title", de: "Hat einen klaren, konkreten Titel" },
  "dashboard.dorCheck.description": {
    en: "Explains what you need and why",
    de: "Erklärt, was du brauchst und warum",
  },
  "dashboard.dorCheck.outcome": { en: "States the business outcome", de: "Nennt den geschäftlichen Nutzen" },
  "dashboard.dorCheck.acceptance": {
    en: "Has at least one acceptance criterion",
    de: "Hat mindestens ein Akzeptanzkriterium",
  },
  // create-progress copy (keyed off the SSE phase enum)
  "dashboard.progress.propose_links": { en: "Re-checking related work", de: "Verwandte Arbeit wird erneut geprüft" },
  "dashboard.progress.dor_check": {
    en: "Checking Definition of Ready",
    de: "Definition of Ready wird geprüft",
  },
  "dashboard.progress.creating": { en: "Creating issue", de: "Issue wird erstellt" },
  "dashboard.progress.working": { en: "Working…", de: "Wird bearbeitet…" },

  // --- IssuePage ---
  "dashboard.issuePage.gate.pass": { en: "Pass", de: "Bestanden" },
  "dashboard.issuePage.gate.fail": { en: "Fail", de: "Fehlgeschlagen" },
  "dashboard.issuePage.backToDashboard": { en: "Back to dashboard", de: "Zurück zum Dashboard" },
  "dashboard.issuePage.loadingIssue": { en: "Loading issue…", de: "Issue wird geladen…" },
  "dashboard.issuePage.new": { en: "New", de: "Neu" },
  "dashboard.issuePage.tellLoom": { en: "Tell Loom what you need", de: "Sag Loom, was du brauchst" },
  "dashboard.issuePage.fileRough": {
    en: "File a rough request — you can sharpen it with Loom right after it's created.",
    de: "Gib erst mal grob ein, was du brauchst – direkt danach schärfst du es mit Loom.",
  },
  "dashboard.issuePage.cancel": { en: "Cancel", de: "Abbrechen" },
  "dashboard.issuePage.creating": { en: "Creating…", de: "Wird erstellt…" },
  "dashboard.issuePage.createRequest": { en: "Create request", de: "Anfrage erstellen" },
  "dashboard.issuePage.unassigned": { en: "Unassigned", de: "Nicht zugewiesen" },
  "dashboard.issuePage.createdUpdated": {
    en: "created {age} · updated {updated}",
    de: "erstellt {age} · aktualisiert {updated}",
  },
  "dashboard.issuePage.openIssue": { en: "Open issue", de: "Issue öffnen" },
  "dashboard.issuePage.unsavedChanges": { en: "Unsaved changes", de: "Ungespeicherte Änderungen" },
  "dashboard.issuePage.discard": { en: "Discard", de: "Verwerfen" },
  "dashboard.issuePage.saving": { en: "Saving…", de: "Wird gespeichert…" },
  "dashboard.issuePage.save": { en: "Save", de: "Speichern" },
  "dashboard.issuePage.dorFailTitle": { en: "DoR fail · {reason}", de: "DoR nicht bestanden · {reason}" },
  "dashboard.issuePage.dorTitle": {
    en: "Definition of Ready: {verdict}",
    de: "Definition of Ready: {verdict}",
  },
  "dashboard.issuePage.dorChip": { en: "DoR · {verdict}", de: "DoR · {verdict}" },
  "dashboard.issuePage.dodTitle": {
    en: "Definition of Done: {verdict}",
    de: "Definition of Done: {verdict}",
  },
  "dashboard.issuePage.dodChip": { en: "DoD · {verdict}", de: "DoD · {verdict}" },
  "dashboard.issuePage.pt": { en: "{points} pt", de: "{points} Pkt" },
  "dashboard.issuePage.dorMissing": { en: "DoR missing — {reason}", de: "DoR fehlt – {reason}" },
  "dashboard.issuePage.loomUpdated": {
    en: "Loom updated this issue while you were editing.",
    de: "Loom hat dieses Issue aktualisiert, während du dran warst.",
  },
  "dashboard.issuePage.reviewChanges": { en: "Review changes", de: "Änderungen prüfen" },
  "dashboard.issuePage.keepMine": { en: "Keep mine", de: "Meine behalten" },
  "dashboard.issuePage.fieldTitle": { en: "Title", de: "Titel" },
  "dashboard.issuePage.titlePlaceholder": { en: "Short, specific title", de: "Kurzer, konkreter Titel" },
  "dashboard.issuePage.description": { en: "Description", de: "Beschreibung" },
  "dashboard.issuePage.hintMarkdown": { en: "— markdown supported", de: "— Markdown möglich" },
  "dashboard.issuePage.descriptionPlaceholder": {
    en: "Describe what you need in markdown — lists, checkboxes, code, tables all work.",
    de: "Beschreib in Markdown, was du brauchst – Listen, Checkboxen, Code und Tabellen gehen alle.",
  },
  "dashboard.issuePage.descriptionAria": { en: "Issue description", de: "Issue-Beschreibung" },
  "dashboard.issuePage.businessOutcome": { en: "Business outcome", de: "Geschäftlicher Nutzen" },
  "dashboard.issuePage.hintWhyMatters": { en: "— why it matters", de: "— warum es zählt" },
  "dashboard.issuePage.outcomePlaceholder": {
    en: "Customers can reconcile VAT without calling support",
    de: "Kunden können die MwSt. ohne Anruf beim Support abgleichen",
  },
  "dashboard.issuePage.acceptanceCriteria": { en: "Acceptance criteria", de: "Akzeptanzkriterien" },
  "dashboard.issuePage.hintHowDone": {
    en: "— how we'll know it's done",
    de: "— woran wir sehen, dass es fertig ist",
  },
  "dashboard.issuePage.acPlaceholder": { en: "Given… when… then…", de: "Gegeben… wenn… dann…" },
  "dashboard.issuePage.addCriterion": { en: "Add criterion", de: "Kriterium hinzufügen" },
  "dashboard.issuePage.priority": { en: "Priority", de: "Priorität" },
  "dashboard.issuePage.readyToBuild": { en: "Ready to build", de: "Baubereit" },
  "dashboard.issuePage.readinessPct": { en: "Readiness {pct}%", de: "Bereitschaft {pct}%" },
  "dashboard.issuePage.meetsDor": {
    en: "Meets the Definition of Ready",
    de: "Erfüllt die Definition of Ready",
  },
  "dashboard.issuePage.checksProgress": {
    en: "{passed} of {total} checks",
    de: "{passed} von {total} Prüfungen",
  },
  "dashboard.issuePage.refineWithLoom": { en: "Refine with Loom", de: "Mit Loom verfeinern" },
  "dashboard.issuePage.refineCreateFirst": {
    en: "Create the request first — then Loom opens right here to sharpen the scope, business outcome, and acceptance criteria with you.",
    de: "Erstelle zuerst die Anfrage – danach öffnet sich Loom direkt hier und schärft mit dir Umfang, geschäftlichen Nutzen und Akzeptanzkriterien.",
  },
  "dashboard.issuePage.tab.refine": { en: "Refine", de: "Verfeinern" },
  "dashboard.issuePage.tab.progression": { en: "Progression", de: "Fortschritt" },
  "dashboard.issuePage.tab.activity": { en: "Activity", de: "Aktivität" },
  "dashboard.issuePage.refineIntro": {
    en: "Loom opens a session, reads through your actual code, then helps you sharpen the scope, business outcome, and acceptance criteria. Its edits land on this issue automatically.",
    de: "Loom öffnet eine Sitzung, liest sich in deinen echten Code ein und hilft dir dann, Umfang, geschäftlichen Nutzen und Akzeptanzkriterien zu schärfen. Seine Änderungen landen automatisch auf diesem Issue.",
  },
  "dashboard.issuePage.startingLoom": { en: "Starting Loom…", de: "Loom wird gestartet…" },
  "dashboard.issuePage.startRefining": { en: "Start refining", de: "Verfeinern starten" },
  "dashboard.issuePage.loomProgression": { en: "Loom progression", de: "Loom-Fortschritt" },
  "dashboard.issuePage.activity": { en: "Activity", de: "Aktivität" },
};
